import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, defaultsForStaffRole } from '@structural-climbing/auth'
import type { StaffPermissions, StaffRole } from '@structural-climbing/auth'
import {
  addToast,
  Button,
  Card,
  CardBody,
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@structural-climbing/ui'
import { useGymContext } from '../contexts/GymContext'
import { tierIncludesCompetitions } from '../lib/tiers'
import { InviteStaffModal } from '../features/team/InviteStaffModal'
import { staffTitles } from '../features/team/staffTitles'
import { PermissionsDialog } from '../features/team/PermissionsDialog'
import { RemoveStaffModal } from '../features/team/RemoveStaffModal'
import { TrainerUnavailableModal } from '../features/trainers/TrainerUnavailableModal'
import { PageHeader } from '../components/PageHeader'
import { MemberIdentity } from '../features/members/MemberIdentity'
import { useIsMobile } from '../lib/useIsMobile'

interface StaffMember {
  id: string
  name: string | null
  email: string
  staff_role: StaffRole | null
  staff_permissions: StaffPermissions | null
}

interface PendingInvite {
  id: string
  email: string
  name: string
  staff_role: StaffRole
  expires_at: string
  /** Past its seven days. Still listed so it can be sent again. */
  expired?: boolean
  /** The link from the email, for when the email itself never arrives. */
  invite_url?: string
}

// The defaults live in @structural-climbing/auth, shared with the sidebar and
// mirrored by the Worker. A local copy used to live here and had already
// drifted — it predated competitions, news, statistics, invoices and exports,
// so the dialog opened with those unticked for a staff member who in fact had
// them.
function resolvePermissions(member: StaffMember): StaffPermissions {
  return member.staff_permissions ?? defaultsForStaffRole(member.staff_role)
}

/** Whether this person has a caseload of their own, whatever their job title. */
function coaches(member: StaffMember): boolean {
  return resolvePermissions(member).my_members === true
}

export function TeamPage() {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const isMobile = useIsMobile()

  const [staff, setStaff] = useState<StaffMember[]>([])
  const [pending, setPending] = useState<PendingInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [permTarget, setPermTarget] = useState<StaffMember | null>(null)
  // A trainer being away is one event across their whole caseload, so it
  // belongs on the person, not on each of their members.
  const [awayTarget, setAwayTarget] = useState<StaffMember | null>(null)
  const [removeTarget, setRemoveTarget] = useState<StaffMember | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)

  const { planTier, productsEnabled, name: gymName } = useGymContext()
  const gymFeatures = {
    productsEnabled: productsEnabled === true,
    competitionsEnabled: tierIncludesCompetitions(planTier),
  }

  const ROLE_LABELS: Record<StaffRole, string> = {
    front_desk: t('team.roleFrontDesk'),
    route_setter: t('team.roleRouteSetter'),
    trainer: t('team.roleTrainer'),
  }

  const fetchStaff = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const [staffResp, pendingResp] = await Promise.all([
        fetch(`${apiUrl}/staff`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/pending-staff-invites`, { headers: { Authorization: `Bearer ${token}` } }),
      ])
      if (staffResp.ok) setStaff((await staffResp.json()) as StaffMember[])
      if (pendingResp.ok) setPending((await pendingResp.json()) as PendingInvite[])
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently, apiUrl])

  useEffect(() => {
    void fetchStaff()
  }, [fetchStaff])

  async function handleCancelInvite(inviteId: string) {
    setCancellingId(inviteId)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/pending-staff-invites/${inviteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (resp.ok) {
        setPending((prev) => prev.filter((i) => i.id !== inviteId))
      }
    } finally {
      setCancellingId(null)
    }
  }

  async function handleResendInvite(invite: PendingInvite) {
    setResendingId(invite.id)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/pending-staff-invites/${invite.id}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (resp.ok) {
        addToast({ title: t('team.inviteResent', { email: invite.email }), color: 'success' })
        void fetchStaff()
        return
      }
      const body = (await resp.json().catch(() => ({}))) as { code?: string }
      addToast({
        title: body.code === 'EMAIL' ? t('team.errorResendEmail') : t('team.errorResendInvite'),
        color: 'danger',
      })
    } catch {
      addToast({ title: t('team.errorResendInvite'), color: 'danger' })
    } finally {
      setResendingId(null)
    }
  }

  async function handleCopyInviteLink(invite: PendingInvite) {
    if (!invite.invite_url) return
    try {
      await navigator.clipboard.writeText(invite.invite_url)
      addToast({ title: t('team.inviteLinkCopied'), color: 'success' })
    } catch {
      addToast({ title: t('team.errorCopyInviteLink'), color: 'danger' })
    }
  }

  function handlePermissionsSaved(staffId: string, permissions: StaffPermissions) {
    setStaff((prev) =>
      prev.map((m) => (m.id === staffId ? { ...m, staff_permissions: permissions } : m)),
    )
  }

  interface Row {
    key: string
    email: string
    /** Avatar and name; on a phone the email goes under the name. */
    identity: (withEmail: boolean) => ReactNode
    badge: ReactNode
    roles: ReactNode
    actions: ReactNode
  }

  const staffRows: Row[] = staff.map((member) => {
    // The job they were invited as, then every other job their permissions now cover.
    const { roles, fullAccess } = staffTitles(
      member.staff_role,
      resolvePermissions(member),
      gymFeatures,
    )
    return {
      key: member.id,
      email: member.email,
      identity: (withEmail) => (
        <MemberIdentity
          userId={null}
          name={member.name ?? member.email}
          email={withEmail ? member.email : null}
        />
      ),
      badge: null,
      roles:
        roles.length === 0 && !fullAccess ? (
          <span className="text-foreground/40">—</span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {roles.map((role) => (
              <Chip key={role} size="sm" variant="flat">
                {ROLE_LABELS[role]}
              </Chip>
            ))}
            {fullAccess && (
              <Chip size="sm" variant="flat" color="primary">
                {t('team.fullAccess')}
              </Chip>
            )}
          </div>
        ),
      actions: (
        <>
          {coaches(member) && (
            <Button size="sm" variant="flat" onPress={() => setAwayTarget(member)}>
              {t('trainers.awayAction')}
            </Button>
          )}
          <Button size="sm" variant="flat" color="primary" onPress={() => setPermTarget(member)}>
            {t('team.permissions')}
          </Button>
          <Button size="sm" variant="flat" color="danger" onPress={() => setRemoveTarget(member)}>
            {t('team.remove')}
          </Button>
        </>
      ),
    }
  })

  const inviteRows: Row[] = pending.map((invite) => ({
    key: invite.id,
    email: invite.email,
    identity: (withEmail) => (
      <div className="min-w-0 opacity-70">
        <MemberIdentity userId={null} name={invite.name} email={withEmail ? invite.email : null} />
      </div>
    ),
    badge: invite.expired ? (
      <Chip size="sm" variant="flat" color="danger">
        {t('team.expiredBadge')}
      </Chip>
    ) : (
      <Chip size="sm" variant="flat" color="warning">
        {t('team.pendingBadge')}
      </Chip>
    ),
    roles: (
      <Chip size="sm" variant="flat">
        {ROLE_LABELS[invite.staff_role]}
      </Chip>
    ),
    actions: (
      <>
        <Button
          size="sm"
          variant="flat"
          color="primary"
          isLoading={resendingId === invite.id}
          onPress={() => void handleResendInvite(invite)}
        >
          {t('team.resendInvite')}
        </Button>
        {/* An expired link refuses, so there is nothing to copy until the
            invite has been sent again. */}
        {invite.invite_url && !invite.expired && (
          <Button size="sm" variant="flat" onPress={() => void handleCopyInviteLink(invite)}>
            {t('team.copyInviteLink')}
          </Button>
        )}
        <Button
          size="sm"
          variant="flat"
          color="danger"
          isLoading={cancellingId === invite.id}
          onPress={() => void handleCancelInvite(invite.id)}
        >
          {t('team.cancelInvite')}
        </Button>
      </>
    ),
  }))

  const rows = [...staffRows, ...inviteRows]

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('team.title')}
        primaryAction={{ label: t('team.invite'), onPress: () => setInviteOpen(true) }}
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : staff.length === 0 && pending.length === 0 ? (
        <p className="text-sm text-foreground/60">{t('team.empty')}</p>
      ) : (
        <Card shadow="sm">
          <CardBody className="p-0">
            {isMobile ? (
              <div className="divide-y divide-divider">
                {rows.map((row) => (
                  <div key={row.key} className="flex flex-col gap-2.5 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      {row.identity(true)}
                      {row.badge}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {row.roles}
                      <span className="flex-1" />
                      {row.actions}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Table
                aria-label={t('team.title')}
                removeWrapper
                classNames={{ th: 'bg-transparent', td: 'align-middle' }}
              >
                <TableHeader>
                  <TableColumn>{t('team.colName')}</TableColumn>
                  <TableColumn>{t('team.colEmail')}</TableColumn>
                  <TableColumn>{t('team.colRole')}</TableColumn>
                  <TableColumn> </TableColumn>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {row.identity(false)}
                          {row.badge}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-foreground/60">{row.email}</TableCell>
                      <TableCell>{row.roles}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">{row.actions}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardBody>
        </Card>
      )}

      <InviteStaffModal
        isOpen={inviteOpen}
        onClose={() => {
          setInviteOpen(false)
          // An invite whose email failed is saved all the same, and has to
          // show up here for Resend and Copy link to be reachable.
          void fetchStaff()
        }}
        onInvited={() => {
          setInviteOpen(false)
          void fetchStaff()
        }}
      />

      {awayTarget && (
        <TrainerUnavailableModal
          trainerId={awayTarget.id}
          trainerName={awayTarget.name ?? awayTarget.email}
          onClose={() => setAwayTarget(null)}
          onDone={() => void fetchStaff()}
        />
      )}

      {removeTarget && (
        <RemoveStaffModal
          staffId={removeTarget.id}
          staffName={removeTarget.name ?? removeTarget.email}
          gymName={gymName}
          onClose={() => setRemoveTarget(null)}
          onRemoved={(staffId) => {
            setRemoveTarget(null)
            setStaff((prev) => prev.filter((m) => m.id !== staffId))
            addToast({
              title: t('team.removed', { name: removeTarget.name ?? removeTarget.email }),
              color: 'success',
            })
          }}
        />
      )}

      {permTarget && (
        <PermissionsDialog
          staffId={permTarget.id}
          staffName={permTarget.name ?? permTarget.email}
          permissions={resolvePermissions(permTarget)}
          onClose={() => setPermTarget(null)}
          onSaved={handlePermissionsSaved}
        />
      )}
    </div>
  )
}
